import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../store/hooks';
import {
  isPushSupported,
  setupForegroundMessageListener,
  syncPushWithBrowserPermission,
  subscribeToPushNotifications,
  clearPushRegistrationCache
} from '../services/pushService';

/** Prevent duplicate token sync when hook mounts in App + other components */
const pushSyncState = { userId: null, inflight: null };

/**
 * Keeps FCM web push in sync with browser Notification.permission.
 * No in-app enable/disable toggle — browser settings are the source of truth.
 */
export function usePushNotifications() {
  const { user } = useAppSelector((state) => state.auth);
  const [pushSupported, setPushSupported] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [syncing, setSyncing] = useState(false);
  const foregroundListenerAttached = useRef(false);

  useEffect(() => {
    isPushSupported().then(setPushSupported);
  }, []);

  const syncWithBrowser = useCallback(async () => {
    if (!user?.id || !pushSupported) {
      return null;
    }

    if (typeof Notification !== 'undefined') {
      setBrowserPermission(Notification.permission);
    }

    if (pushSyncState.inflight && pushSyncState.userId === user.id) {
      return pushSyncState.inflight;
    }

    const run = async () => {
      setSyncing(true);
      try {
        const result = await syncPushWithBrowserPermission();
        if (typeof Notification !== 'undefined') {
          setBrowserPermission(Notification.permission);
        }
        return result;
      } catch (err) {
        console.warn('Push sync skipped:', err?.message || err);
        return null;
      } finally {
        setSyncing(false);
        if (pushSyncState.userId === user.id) {
          pushSyncState.inflight = null;
        }
      }
    };

    pushSyncState.userId = user.id;
    pushSyncState.inflight = run();
    return pushSyncState.inflight;
  }, [user?.id, pushSupported]);

  useEffect(() => {
    if (!user?.id || !pushSupported || foregroundListenerAttached.current) {
      return;
    }
    foregroundListenerAttached.current = true;
    setupForegroundMessageListener().catch(() => {
      foregroundListenerAttached.current = false;
    });
  }, [user?.id, pushSupported]);

  useEffect(() => {
    if (!user?.id || !pushSupported) {
      return undefined;
    }

    const token = sessionStorage.getItem('token');
    if (token) {
      syncWithBrowser();
    }

    if (typeof Notification === 'undefined' || !navigator.permissions?.query) {
      return undefined;
    }

    let permissionStatus = null;

    navigator.permissions
      .query({ name: 'notifications' })
      .then((status) => {
        permissionStatus = status;
        setBrowserPermission(Notification.permission);

        status.onchange = () => {
          setBrowserPermission(Notification.permission);
          if (user?.id && sessionStorage.getItem('token')) {
            syncWithBrowser();
          }
        };
      })
      .catch(() => {});

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [user?.id, pushSupported, syncWithBrowser]);

  useEffect(() => {
    if (!user) {
      pushSyncState.userId = null;
      pushSyncState.inflight = null;
      clearPushRegistrationCache();
    }
  }, [user]);

  const requestBrowserPermission = useCallback(async () => {
    setSyncing(true);
    try {
      await subscribeToPushNotifications();
      if (typeof Notification !== 'undefined') {
        setBrowserPermission(Notification.permission);
      }
    } finally {
      setSyncing(false);
    }
  }, []);

  return {
    pushSupported,
    pushEnabled: browserPermission === 'granted',
    browserPermission,
    syncing,
    requestBrowserPermission,
    syncWithBrowser
  };
}

export default usePushNotifications;
